import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Box,
  Avatar,
  Typography,
  Button,
  Grid,
  Divider,
  Chip,
  Stack,
} from "@material-ui/core";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import { formatearFecha } from "src/utils/formatTime";

const formatearFechaIngreso = (fecha) => {
  if (!fecha) return null;
  try {
    return formatearFecha(fecha, "DD/MM/YYYY", "YYYY/MM/DD");
  } catch {
    return fecha;
  }
};

const Dato = ({ label, value }) => (
  <Box>
    <Typography
      variant="overline"
      sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: "text.primary" }}>
      {value || "—"}
    </Typography>
  </Box>
);

Dato.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

const VistaPerfilEmpleado = ({ empleado, onEdit, esVisible, puedeEditar }) => {
  if (!esVisible) return null;
  if (!empleado) return null;

  const iniciales = empleado.nombre
    ? empleado.nombre.charAt(0).toUpperCase()
    : "?";
  const especialidades = (empleado.especialidades || "")
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <Avatar
            src={empleado.foto_url}
            sx={{ width: 96, height: 96, bgcolor: "#d57a23", fontSize: 36 }}
          >
            {iniciales}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {empleado.nombre} {empleado.apellidos}
                </Typography>
                {empleado.cargo && (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#d57a23",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {empleado.cargo}
                  </Typography>
                )}
              </Box>
              {puedeEditar && (
                <Button variant="contained" onClick={onEdit}>
                  Editar
                </Button>
              )}
            </Box>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Dato
                  label="RUT"
                  value={formatRut(empleado.rut, RutFormat.DASHS)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Dato label="Correo" value={empleado.correo} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Dato label="Teléfono" value={empleado.telefono} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Dato label="Estado" value={empleado.estado} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Dato
                  label="Fecha de ingreso"
                  value={formatearFechaIngreso(empleado.fechaIngreso)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Dato
                  label="Años de experiencia"
                  value={empleado.anios_experiencia}
                />
              </Grid>
            </Grid>

            {(empleado.bio || especialidades.length > 0) && (
              <Divider sx={{ my: 2 }} />
            )}

            {empleado.bio && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Bio profesional
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.primary", whiteSpace: "pre-line" }}
                >
                  {empleado.bio}
                </Typography>
              </Box>
            )}

            {especialidades.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Especialidades
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}
                >
                  {especialidades.map((esp) => (
                    <Chip
                      key={esp}
                      label={esp}
                      size="small"
                      sx={{ bgcolor: "#f4ede5", color: "#8c5419" }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

VistaPerfilEmpleado.propTypes = {
  empleado: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  esVisible: PropTypes.bool.isRequired,
  puedeEditar: PropTypes.bool.isRequired,
};

export default VistaPerfilEmpleado;
